import { HealthcareService, Location } from "fhir/r3"
import { Hero } from "nhsuk-react-components"
import { useContext, useEffect, useMemo, useState } from "react"
import { Link, useParams } from "react-router-dom"
import { useAppointmentQuery } from "../../api/fetchAppointment"
import { useHealthcareServiceQuery } from "../../api/fetchHealthcareService"
import { SimplifiedAppointment } from "../../appointment.types"
import { ContactCard } from "../../components/careCard/ContactCard"
import { InformationBlock } from "../../components/informationBlock/InformationBlock"
import { Loading } from "../../components/loading/Loading"
import { AuthenticationContext } from "../../context/AuthenticationContext"
import { BackButtonContext } from "../../context/BackButtonContext"
import { useNhsAppJs } from "../../hooks/useNhsAppJs"
import { PageFunction } from "../../types"
import { AppointmentCard } from "../appointment/appointmentViews/AppointmentCard"
import { queryClient } from "../../utils/queryClient"
import { useLocationQuery } from "../../api/fetchLocation"

/**
 * Questionnaire Flow:
 *
 * `QuestionnaireStart.tsx` -> (`QuestionPage.tsx` -> `...` -> `QuestionPage.tsx`) -> `QuestionnaireSummary.tsx` -> **QuestionnaireComplete.tsx**
 *
 * This page:
 * - shows that the questionnaire has been completed
 *
 * @returns {JSX.Element} The rendered page.
 */
export const QuestionnaireComplete = (): JSX.Element => {
  const params = useParams()

  const { setShowBackButton } = useContext(BackButtonContext)

  const { token, setChangeRequested } = useContext(AuthenticationContext)

  const { inApp, navigateToPage } = useNhsAppJs()

  const pageFunction: PageFunction = useMemo(
    () => (params.patientAction === "reschedule" ? "reschedule" : "cancel"),
    [params.patientAction],
  )

  const [hasRefetched, setHasRefetched] = useState(false)

  useEffect(() => {
    if (!setShowBackButton) return
    setChangeRequested(true)
    setShowBackButton(false)
  }, [setChangeRequested, setShowBackButton])

  const appointmentQuery = useAppointmentQuery({
    appointmentId: params.appointmentId,
    token,
  })

  const healthcareServiceQuery = useHealthcareServiceQuery({
    odsId: params.odsId,
    token,
  })
  const appointment = appointmentQuery.data

  const locationId = appointment?.locationId

  useEffect(() => {
    if (!hasRefetched && params.appointmentId && params.odsId) {
      queryClient.invalidateQueries({ queryKey: ["appointment", params.appointmentId] })
      queryClient.invalidateQueries({ queryKey: ["healthcareService", params.odsId] })
      setHasRefetched(true)
    }
  }, [appointmentQuery, hasRefetched, healthcareServiceQuery, params.appointmentId, params.odsId])
  const { data: location } = useLocationQuery({ locationId, token })

  if (appointmentQuery.isLoading || healthcareServiceQuery.isLoading) {
    return <Loading />
  }

  if (appointmentQuery.isError || healthcareServiceQuery.isError) {
    navigateToPage("error")
    return <></>
  }

  const healthcareService = healthcareServiceQuery.data
  if (!healthcareService || !appointment) {
    navigateToPage("error")
    return <></>
  }

  return (
    <QuestionnaireCompleteContent
      inApp={!!inApp}
      appointmentId={params.appointmentId}
      location={location}
      odsId={params.odsId}
      appointment={appointment}
      pageFunction={pageFunction}
      healthcareService={healthcareService}
      onAllAppointmentsClick={() => navigateToPage("appointments")}
    />
  )
}

type QuestionnaireCompleteContentProps = {
  odsId?: string
  appointmentId?: string
  appointment: SimplifiedAppointment
  location?: Location
  pageFunction: PageFunction
  healthcareService: HealthcareService
  inApp: boolean
  onAllAppointmentsClick: () => void
}

const CANCEL_WHAT_HAPPENS_NOW_BULLETS = [
  "The booking team will review and process your request in approximately 2 working days.",
  "The cancelled appointment will remain visible until the date has passed.",
  "The booking team will contact you if there are problems with your cancellation request.",
]

const RESCHEDULE_WHAT_HAPPENS_NOW_BULLETS = [
  "The booking team will process your request in approximately 2 working days.",
  "They may contact you to confirm the new appointment.",
  "The original appointment will remain visible until the date has passed.",
]

export const QuestionnaireCompleteContent = ({
  appointment,
  location,
  healthcareService,
  pageFunction,
  odsId,
  appointmentId,
  inApp,
  onAllAppointmentsClick,
}: QuestionnaireCompleteContentProps) => {
  // We should only show the "change your mind" bullet point if the user has cancelled.
  const contactGpBullets = ["your condition changes", "you think you have a different condition"]
  if (pageFunction === "cancel") {
    contactGpBullets.push("you change your mind and need care")
  }

  const whatHappensNowBullets =
    pageFunction === "cancel" ? CANCEL_WHAT_HAPPENS_NOW_BULLETS : RESCHEDULE_WHAT_HAPPENS_NOW_BULLETS

  const pageHeader = `Your request to ${pageFunction} has been submitted`
  return (
    <>
      <div>
        <Hero className="complete">
          <h1>{pageHeader}</h1>
        </Hero>
        <AppointmentCard
          appointment={appointment}
          healthcareService={healthcareService}
          appointmentView={false}
          location={location}
        />
      </div>
      <InformationBlock header="What happens now" information={whatHappensNowBullets} />
      <ContactCard contact="your care provider" bullets={contactGpBullets} />
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          gap: "24px",
        }}
      >
        <Link to={`/appointment/${odsId}/${appointmentId}`}>Go to your appointment</Link>
        {inApp && <a onClick={onAllAppointmentsClick}>All appointments</a>}
      </div>
    </>
  )
}
